/**
 * Worker client - singleton geo.worker with a Promise-based API.
 *
 * Auto-routes:
 *   - Small datasets (< WORKER_THRESHOLD_FEATURES): run inline (no IPC overhead).
 *   - Larger: dispatch to the geo worker (UI stays responsive).
 *
 * Usage:
 *   const result = await runGeo('buffer', { geojson, options });
 */

import type { FeatureCollection, Polygon, MultiPolygon } from "geojson";
import type { Units } from "@turf/turf";
import { bufferFeatures } from "@/lib/geoprocessing/buffer";
import { simplifyGeometries } from "@/lib/geoprocessing/simplify";
import { reprojectGeoJSON } from "@/lib/geoprocessing/reproject";
import { clipByBBox, clipByPolygon } from "@/lib/geoprocessing/clip";

const WORKER_THRESHOLD_FEATURES = 5_000;

// Vite worker import (`?worker` suffix)
// Lazily instantiated so first paint is faster.
let _worker: Worker | null = null;
function getWorker(): Worker {
  if (!_worker) {
    _worker = new Worker(new URL("@/workers/geo.worker.ts", import.meta.url), { type: "module" });
  }
  return _worker;
}

interface GeoOps {
  buffer:      { in: { geojson: FeatureCollection; options: { distance: number; units: Units; steps?: number } };
                 out: FeatureCollection };
  simplify:    { in: { geojson: FeatureCollection; options: { tolerance: number; highQuality?: boolean } };
                 out: unknown };
  reproject:   { in: { geojson: FeatureCollection; fromEPSG: number; toEPSG: number };
                 out: FeatureCollection };
  clipBBox:    { in: { geojson: FeatureCollection; bbox: [number, number, number, number] };
                 out: unknown };
  clipPolygon: { in: { geojson: FeatureCollection; mask: Polygon | MultiPolygon };
                 out: unknown };
}

let idCounter = 0;

function runInWorker<O extends keyof GeoOps>(op: O, payload: GeoOps[O]["in"]): Promise<GeoOps[O]["out"]> {
  return new Promise((resolve, reject) => {
    const id = `${op}-${++idCounter}-${Date.now()}`;
    const w = getWorker();
    const onMsg = (ev: MessageEvent<{ id: string; type: string; data: unknown }>) => {
      if (ev.data.id !== id) return;
      w.removeEventListener("message", onMsg);
      if (ev.data.type === "result") resolve(ev.data.data as GeoOps[O]["out"]);
      else reject(Object.assign(new Error((ev.data.data as Error).message), { name: (ev.data.data as Error).name }));
    };
    w.addEventListener("message", onMsg);
    w.postMessage({ id, op, payload });
  });
}

export async function runGeo<O extends keyof GeoOps>(
  op: O,
  payload: GeoOps[O]["in"]
): Promise<GeoOps[O]["out"]> {
  const featureCount = "geojson" in payload ? payload.geojson?.features?.length ?? 0 : 0;
  const heavy = featureCount >= WORKER_THRESHOLD_FEATURES;

  if (!heavy) {
    // Inline path - preserves sub-ms latency for small datasets and keeps test parity.
    switch (op) {
      case "buffer":      return bufferFeatures((payload as GeoOps["buffer"]["in"]).geojson, (payload as GeoOps["buffer"]["in"]).options) as GeoOps[O]["out"];
      case "simplify":    return simplifyGeometries((payload as GeoOps["simplify"]["in"]).geojson, (payload as GeoOps["simplify"]["in"]).options) as GeoOps[O]["out"];
      case "reproject":   return reprojectGeoJSON((payload as GeoOps["reproject"]["in"]).geojson, (payload as GeoOps["reproject"]["in"]).fromEPSG, (payload as GeoOps["reproject"]["in"]).toEPSG) as GeoOps[O]["out"];
      case "clipBBox":    return clipByBBox((payload as GeoOps["clipBBox"]["in"]).geojson, (payload as GeoOps["clipBBox"]["in"]).bbox) as GeoOps[O]["out"];
      case "clipPolygon": return clipByPolygon((payload as GeoOps["clipPolygon"]["in"]).geojson, (payload as GeoOps["clipPolygon"]["in"]).mask) as GeoOps[O]["out"];
    }
  }
  return runInWorker(op, payload);
}

/** Terminate the worker when the tab is hidden for memory-recovery. */
export function disposeWorker() {
  if (_worker) {
    _worker.terminate();
    _worker = null;
  }
}
